import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { FULL_MONTHS, qaStatusColor, qaStatus, thisMonth, thisYear, classNames } from "../lib/utils";
import { Save, Trash2, AlertTriangle, UserCircle2, Camera } from "lucide-react";
import AISummaryCard from "../components/AISummaryCard";
import QAPhotoStrip from "../components/QAPhotoStrip";

export default function QAAuditEntryPage() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [homes, setHomes] = useState([]);
  const hmHomes = (user.assigned_homes && user.assigned_homes.length ? user.assigned_homes : (user.assigned_home ? [user.assigned_home] : []));
  const isMultiHomeHM = user.access_role === "House Manager" && hmHomes.length > 1;
  const [home, setHome] = useState(user.access_role === "House Manager" ? (hmHomes[0] || "") : "");
  const [month, setMonth] = useState(thisMonth());
  const [year, setYear] = useState(thisYear());
  const [scoreMap, setScoreMap] = useState({});
  const [managerNotes, setManagerNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [auditExists, setAuditExists] = useState(false);
  const [homeManager, setHomeManager] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePhrase, setDeletePhrase] = useState("");
  const [deleting, setDeleting] = useState(false);

  const todayISO = () => new Date().toISOString().slice(0, 10);
  const [auditEntryDate, setAuditEntryDate] = useState(todayISO());
  const canEditDelete = ["CEO", "Project Manager", "Administration", "House Manager"].includes(user.access_role);
  const canUploadPhotos = ["CEO", "Project Manager", "Administration", "House Manager"].includes(user.access_role);

  const [photosByMetric, setPhotosByMetric] = useState({});
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const totalPhotos = galleryPhotos.length + Object.values(photosByMetric).reduce((s, arr) => s + arr.length, 0);

  const reloadPhotos = useCallback(() => {
    if (!home) { setPhotosByMetric({}); setGalleryPhotos([]); return; }
    api.get("/qa/photos", { params: { home, month, year } }).then(({ data }) => {
      setGalleryPhotos(data.gallery || []);
      setPhotosByMetric(data.by_metric || {});
    }).catch(() => { /* swallow — page still functional without photos */ });
  }, [home, month, year]);

  useEffect(() => {
    api.get("/qa/catalog").then(({ data }) => setCatalog(data));
    api.get("/homes").then(({ data }) => setHomes(data));
  }, []);

  const reload = useCallback(() => {
    if (!home) {
      setScoreMap({}); setManagerNotes(""); setAuditExists(false); setHomeManager(null);
      setAuditEntryDate(todayISO());
      return;
    }
    api.get("/qa/audit", { params: { home, month, year } }).then(({ data }) => {
      const map = {};
      (data.metric_scores || []).forEach((s) => {
        map[s.metric_id] = { score_percent: s.score_percent, notes: s.notes };
      });
      setScoreMap(map);
      setManagerNotes(data.audit?.manager_notes || "");
      setAuditExists(!!data.audit);
      setHomeManager(data.house_manager || null);
      // On edit, preserve the previously-saved audit entry date. Never silently bump
      // it to today just because someone re-opened the page.
      setAuditEntryDate(data.audit?.audit_entry_date || todayISO());
    });
  }, [home, month, year]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { reloadPhotos(); }, [reloadPhotos]);

  const update = (metricId, patch) => setScoreMap((m) => ({ ...m, [metricId]: { ...(m[metricId] || {}), ...patch } }));

  const save = async () => {
    if (!home) return toast.error("Select a home first.");
    setSaving(true);
    try {
      // Each metric record inherits the top-level Audit Entry Date on the server.
      const metric_scores = Object.entries(scoreMap).map(([metric_id, v]) => ({
        metric_id,
        score_percent: v.score_percent,
        notes: v.notes,
      }));
      const { data } = await api.post("/qa/audit", {
        home, audit_month: +month, audit_year: +year,
        manager_notes: managerNotes, metric_scores,
        audit_entry_date: auditEntryDate,
      });
      toast.success(
        `${data.is_update ? "QA Audit updated" : "QA Audit saved"} · ${data.audit.overall_score_percent}% ${data.audit.overall_status}`
      );
      setAuditExists(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const openDelete = () => { setDeletePhrase(""); setDeleteOpen(true); };
  const confirmDelete = async () => {
    if (deletePhrase.trim() !== "DELETE QA AUDIT") {
      return toast.error("Type DELETE QA AUDIT exactly to confirm.");
    }
    setDeleting(true);
    try {
      const { data } = await api.delete("/qa/audit", {
        params: { home, month: +month, year: +year, confirm: "DELETE QA AUDIT" }
      });
      toast.success(`QA Audit deleted (${data.metric_scores_deleted} metric scores cleared)`);
      setDeleteOpen(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const liveOverall = (() => {
    if (!catalog.length) return 0;
    const cats = catalog.map((c) => {
      const vals = c.metrics.map((m) => parseFloat(scoreMap[m.id]?.score_percent)).filter((v) => !isNaN(v));
      return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
    }).filter((v) => v !== null);
    return cats.length ? +(cats.reduce((a, b) => a + b, 0) / cats.length).toFixed(1) : 0;
  })();
  const liveStatus = qaStatus(liveOverall);

  return (
    <div className="p-6 sm:p-8 space-y-6 pb-24">
      <div>
        <p className="text-small-caps">Home Metrics</p>
        <h1 className="text-h1 mt-1">QA Audit Entry</h1>
        <p className="text-sm text-slate-500 mt-1">Quality audit of an entire group home (separate from staff KPI scoring).</p>
      </div>

      <div className="nv-card p-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="text-small-caps">Home</label>
            <select value={home} onChange={(e) => setHome(e.target.value)} disabled={user.access_role === "House Manager" && !isMultiHomeHM} className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2 disabled:bg-slate-50" data-testid="qa-home-select">
              <option value="">Select home…</option>
              {homes.map((h) => <option key={h.home_name} value={h.home_name}>{h.home_name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-small-caps">Month</label>
            <select value={month} onChange={(e) => setMonth(+e.target.value)} className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2">
              {FULL_MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="text-small-caps">Year</label>
            <input type="number" value={year} onChange={(e) => setYear(+e.target.value)} className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2" />
          </div>
          <div>
            <label className="text-small-caps" title="The actual date this audit was performed. The selected Month/Year above is the period the audit is for.">
              Audit Entry Date
            </label>
            <input type="date" value={auditEntryDate} onChange={(e) => setAuditEntryDate(e.target.value)}
              className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2"
              data-testid="qa-audit-entry-date" />
            <p className="text-[11.5px] text-slate-400 mt-1">Defaults to today · adjustable</p>
          </div>
        </div>

        {/* Home + manager + edit-mode line */}
        {home && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" data-testid="qa-home-meta">
            <p className="text-slate-700">
              <span className="font-semibold">{home}</span> — {year}
            </p>
            <p className="text-slate-600 inline-flex items-center gap-1.5" data-testid="qa-home-manager">
              <UserCircle2 size={14} className="text-slate-400" />
              House Manager:&nbsp;
              {homeManager?.manager_name ? (
                <>
                  <span className="font-medium text-slate-800">{homeManager.manager_name}</span>
                  {homeManager.manager_active === false && (
                    <span className="ml-1 inline-flex items-center rounded-full bg-rose-50 text-rose-700 border border-rose-200 px-1.5 py-0 text-[11.5px] font-semibold">
                      Inactive
                    </span>
                  )}
                </>
              ) : (
                <span className="italic text-slate-500">Unassigned</span>
              )}
            </p>
            {auditExists && (
              <span
                className="inline-flex items-center rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 text-[12.5px] font-semibold"
                data-testid="qa-editing-existing-banner"
              >
                Editing Existing QA Audit
              </span>
            )}
          </div>
        )}
      </div>

      <div className="nv-card p-5 sticky top-2 z-20 bg-gradient-to-br from-sky-50 to-emerald-50 border-2 border-sky-100" data-testid="qa-live-banner">
        <div className="flex items-baseline gap-3">
          <p className="text-small-caps text-sky-700">Live Overall QA Score</p>
        </div>
        <div className="flex items-baseline gap-3 mt-1">
          <span className="text-4xl font-heading font-bold text-slate-900 tabular-nums">{liveOverall}%</span>
          <span className={classNames("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border", qaStatusColor(liveStatus))}>{liveStatus}</span>
        </div>
      </div>

      <div className="space-y-5">
        {catalog.map((cat) => (
          <div key={cat.id} className="nv-card overflow-hidden" data-testid={`qa-cat-${cat.sort_order}`}>
            <div className="px-5 py-3 bg-slate-50 border-b border-slate-100"><h3 className="text-h3">{cat.category_name}</h3></div>
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-5 py-2.5 text-left">Metric</th>
                  <th className="px-2 py-2.5 text-left">Target</th>
                  <th className="px-2 py-2.5 text-left">Score</th>
                  <th className="px-2 py-2.5 text-left">Status</th>
                  <th className="px-2 py-2.5 text-left">Notes</th>
                  <th className="px-2 py-2.5 text-left">Photos</th>
                </tr>
              </thead>
              <tbody>
                {cat.metrics.map((m) => {
                  const s = scoreMap[m.id] || {};
                  const pct = parseFloat(s.score_percent);
                  const status = qaStatus(pct);
                  const metricPhotos = photosByMetric[m.id] || [];
                  return (
                    <tr key={m.id} className="border-t border-slate-100">
                      <td className="px-5 py-2 font-medium text-slate-800">{m.metric_name}</td>
                      <td className="px-2 py-2 text-slate-500">{m.target}</td>
                      <td className="px-2 py-2"><input type="number" min="0" max="100" value={s.score_percent ?? ""} onChange={(e) => update(m.id, { score_percent: e.target.value })} className="w-20 text-sm rounded-md border border-slate-200 px-2 py-1.5" data-testid={`qa-metric-${m.id}-score`} /></td>
                      <td className="px-2 py-2">{status && <span className={classNames("inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border", qaStatusColor(status))}>{status}</span>}</td>
                      <td className="px-2 py-2"><input value={s.notes || ""} onChange={(e) => update(m.id, { notes: e.target.value })} className="w-full text-sm rounded-md border border-slate-200 px-2 py-1.5" placeholder="Notes / observations" /></td>
                      <td className="px-2 py-2">
                        {home ? (
                          <QAPhotoStrip
                            home={home} month={month} year={year} metricId={m.id}
                            photos={metricPhotos} onChange={reloadPhotos}
                            canEdit={canUploadPhotos && totalPhotos < 20}
                          />
                        ) : (
                          <span className="text-xs text-slate-400">Select a home first</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {home && (
        <div className="nv-card p-5" data-testid="qa-audit-gallery">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Camera size={18} className="text-sky-700" />
              <h3 className="text-h3">Audit Evidence Gallery</h3>
            </div>
            <span className="text-xs text-slate-500">{totalPhotos}/20 photos · {galleryPhotos.length} general</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">Add general audit-level photos that aren't tied to a specific metric (e.g., facility overview, exterior shots).</p>
          <div className="mt-3">
            <QAPhotoStrip
              home={home} month={month} year={year}
              photos={galleryPhotos} onChange={reloadPhotos}
              canEdit={canUploadPhotos && totalPhotos < 20}
            />
          </div>
        </div>
      )}

      <div className="nv-card p-5">
        <h3 className="text-h3">Manager Notes</h3>
        <textarea value={managerNotes} onChange={(e) => setManagerNotes(e.target.value)} rows={3} className="mt-3 w-full text-sm rounded-lg border border-slate-200 bg-white p-3" placeholder="Audit summary, action items, follow-ups…" />
      </div>

      {auditExists && home && (
        <AISummaryCard
          title="AI QA Summary & Recommendations"
          endpoint="/ai/qa-summary"
          payload={{ home, audit_month: +month, audit_year: +year }}
          testIdPrefix="ai-qa-summary"
          render={(d) => (
            <div className="space-y-3">
              {d.executive_summary && <p>{d.executive_summary}</p>}
              {/* AI summary lists are append-only; regenerate replaces the whole `d` object, so index is stable for that render. */}
              {Array.isArray(d.weakest_categories) && d.weakest_categories.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1">Weakest categories</p>
                  <ul className="list-disc ml-5 space-y-0.5">
                    {d.weakest_categories.map((w, i) => <li key={`weak-${i}`}><strong>{w.category}</strong> ({w.score}%) — {w.why}</li>)}
                  </ul>
                </div>
              )}
              {Array.isArray(d.recommendations) && d.recommendations.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1">Recommendations</p>
                  <ul className="list-disc ml-5 space-y-0.5">{d.recommendations.map((r, i) => <li key={`rec-${i}`}>{r}</li>)}</ul>
                </div>
              )}
              {Array.isArray(d.action_items) && d.action_items.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1">Action items</p>
                  <ul className="list-disc ml-5 space-y-0.5">{d.action_items.map((a, i) => <li key={`action-${i}`}>{a}</li>)}</ul>
                </div>
              )}
              {Array.isArray(d.compliance_concerns) && d.compliance_concerns.length > 0 && (
                <div className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2">
                  <p className="text-xs uppercase tracking-wider font-semibold text-rose-700 mb-1">Compliance concerns</p>
                  <ul className="list-disc ml-5 space-y-0.5 text-rose-900">{d.compliance_concerns.map((c, i) => <li key={`comp-${i}`}>{c}</li>)}</ul>
                </div>
              )}
            </div>
          )}
        />
      )}

      <div className="fixed bottom-4 inset-x-0 lg:left-64 px-6 z-30 no-print pointer-events-none">
        <div className="max-w-5xl mx-auto pointer-events-auto">
          <div className="rounded-xl bg-slate-900 text-white shadow-2xl border border-slate-800 p-3 flex items-center gap-3">
            <p className="text-sm">Live: <span className="font-bold tabular-nums">{liveOverall}%</span> · {liveStatus}</p>
            {auditExists && canEditDelete && (
              <button
                onClick={openDelete}
                className="inline-flex items-center gap-1.5 bg-rose-700 hover:bg-rose-800 text-white px-3 py-2 rounded-lg text-sm font-semibold"
                data-testid="qa-delete-btn"
              >
                <Trash2 size={14} /> Delete QA Audit
              </button>
            )}
            <button onClick={save} disabled={saving || !home} className="ml-auto inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-semibold" data-testid="qa-save-btn">
              <Save size={15} /> {saving ? "Saving…" : (auditExists ? "Update QA Audit" : "Save QA Audit")}
            </button>
          </div>
        </div>
      </div>

      {deleteOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={() => !deleting && setDeleteOpen(false)} data-testid="qa-delete-modal">
          <div className="nv-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-rose-700 font-bold">
              <AlertTriangle size={20} />
              <h2 className="text-h2">Delete QA Audit?</h2>
            </div>
            <p className="mt-3 text-sm text-slate-700">
              This will permanently delete the QA Audit for <strong>{home}</strong> · <strong>{FULL_MONTHS[month - 1]} {year}</strong>, including all QA metric scores and notes. <span className="font-semibold text-rose-700">This action cannot be undone.</span>
            </p>
            <label className="block mt-4">
              <span className="text-small-caps">To confirm, type <code className="bg-slate-100 px-1 rounded text-xs">DELETE QA AUDIT</code></span>
              <input
                type="text"
                value={deletePhrase}
                onChange={(e) => setDeletePhrase(e.target.value)}
                autoFocus
                placeholder="DELETE QA AUDIT"
                className="mt-1.5 w-full text-sm rounded-md border border-slate-200 px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-rose-500/30"
                data-testid="qa-delete-phrase-input"
              />
            </label>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setDeleteOpen(false)} disabled={deleting} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50" data-testid="qa-delete-cancel">Cancel</button>
              <button
                onClick={confirmDelete}
                disabled={deleting || deletePhrase.trim() !== "DELETE QA AUDIT"}
                className="px-4 py-2 rounded-md text-sm font-semibold bg-rose-700 hover:bg-rose-800 text-white disabled:opacity-50"
                data-testid="qa-delete-confirm-btn"
              >
                {deleting ? "Deleting…" : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
